import { Vehicle } from '@/types';
import jsPDF from 'jspdf';

/**
 * Génère un PDF de la fiche véhicule
 */
export const generateVehiclePDF = async (vehicle: Vehicle): Promise<void> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let yPos = 20;

  // En-tête avec logo (si disponible)
  doc.setFontSize(20);
  doc.setTextColor(0, 102, 204); // Bleu Trucks52
  doc.text('Trucks 52 SARL', margin, yPos);
  yPos += 10;

  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text('2 Place de l\'Église, Recourt - 52140 Val-de-Meuse', margin, yPos);
  yPos += 5;
  doc.text('Tél: 06 08 24 85 29 | Email: trucks.52@orange.fr', margin, yPos);
  yPos += 5;
  doc.text('SIRET: 508 009 495 00013', margin, yPos);
  yPos += 10;

  // Titre du véhicule
  doc.setFontSize(16);
  doc.setTextColor(0);
  doc.text(vehicle.title, margin, yPos);
  yPos += 10;

  // Ligne de séparation
  doc.setDrawColor(0, 102, 204);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // Prix
  doc.setFontSize(14);
  doc.setTextColor(0, 102, 204);
  const price = vehicle.priceTTC || vehicle.price;
  if (price) {
    doc.text(`Prix: ${price.toLocaleString('fr-FR')} €`, margin, yPos);
    if (vehicle.priceTTC && vehicle.priceHT) {
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`(${vehicle.priceHT.toLocaleString('fr-FR')} € HT)`, margin + 60, yPos);
    }
  }
  yPos += 10;

  // Informations principales
  doc.setFontSize(12);
  doc.setTextColor(0);
  doc.text('Informations principales', margin, yPos);
  yPos += 7;

  doc.setFontSize(10);
  const mainInfo = [
    { label: 'Année', value: vehicle.year && vehicle.year !== 'avant-1990' ? vehicle.year.toString() : 'Avant 1990' },
    { label: 'Kilométrage', value: vehicle.mileage ? `${vehicle.mileage.toLocaleString('fr-FR')} km` : '-' },
    { label: 'Carburant', value: vehicle.fuelType || '-' },
    { label: 'Transmission', value: vehicle.transmission || '-' },
    { label: 'Puissance', value: vehicle.enginePower ? `${vehicle.enginePower} ch` : '-' },
    { label: 'Norme Euro', value: vehicle.euroStandard || '-' },
    { label: 'Crit\'Air', value: vehicle.critAir ? (vehicle.critAir === 'exempt' ? 'Exempté' : `Crit'Air ${vehicle.critAir}`) : '-' },
  ];

  mainInfo.forEach(info => {
    doc.setTextColor(100);
    doc.text(`${info.label}:`, margin, yPos);
    doc.setTextColor(0);
    doc.text(info.value, margin + 40, yPos);
    yPos += 6;
  });

  yPos += 5;

  // Caractéristiques techniques
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(12);
  doc.text('Caractéristiques techniques', margin, yPos);
  yPos += 7;

  doc.setFontSize(10);
  const techSpecs = [
    { label: 'Configuration', value: vehicle.axleConfiguration || '-' },
    { label: 'PTAC', value: vehicle.maxWeight ? `${vehicle.maxWeight.toLocaleString('fr-FR')} kg` : '-' },
    { label: 'Charge utile', value: vehicle.payloadCapacity ? `${vehicle.payloadCapacity.toLocaleString('fr-FR')} kg` : '-' },
    { label: 'Cylindrée', value: vehicle.engineDisplacement ? `${vehicle.engineDisplacement} L` : '-' },
  ];

  techSpecs.forEach(spec => {
    doc.setTextColor(100);
    doc.text(`${spec.label}:`, margin, yPos);
    doc.setTextColor(0);
    doc.text(spec.value, margin + 40, yPos);
    yPos += 6;
  });

  yPos += 5;

  // Description
  if (vehicle.description) {
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(12);
    doc.text('Description', margin, yPos);
    yPos += 7;

    doc.setFontSize(10);
    const splitDescription = doc.splitTextToSize(vehicle.description, pageWidth - 2 * margin);
    doc.text(splitDescription, margin, yPos);
    yPos += splitDescription.length * 5 + 10;
  }

  // Équipements
  const equipment = [];
  if (vehicle.hasAirConditioning) equipment.push('Climatisation');
  if (vehicle.hasAbs) equipment.push('ABS');
  if (vehicle.hasEsp) equipment.push('ESP');
  if (vehicle.hasCruiseControl) equipment.push('Régulateur de vitesse');
  if (vehicle.hasNavigation) equipment.push('Navigation');
  if (vehicle.hasReversingCamera) equipment.push('Caméra de recul');
  if (vehicle.additionalEquipment) equipment.push(...vehicle.additionalEquipment);

  if (equipment.length > 0) {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(12);
    doc.text('Équipements', margin, yPos);
    yPos += 7;

    doc.setFontSize(10);
    equipment.forEach(item => {
      doc.text(`• ${item}`, margin + 5, yPos);
      yPos += 5;
    });
  }

  // Pied de page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Trucks 52 SARL - Fiche véhicule - Page ${i}/${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  // Télécharger le PDF
  const fileName = `Trucks52-${vehicle.brand || 'Vehicule'}-${vehicle.year || ''}-${vehicle.id.slice(0, 8)}.pdf`;
  doc.save(fileName);
};
